import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Sun, Moon } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { LANGS } from "@/i18n/translations";
import { scrollToSection } from "@/lib/scroll";
import { useTheme } from "@/theme/ThemeContext";
import Logo from "@/components/Logo";
import AccountMenu from "@/components/AccountMenu";

const NAV = [
  { id: "home", key: "nav.home" },
  { id: "about", key: "nav.about" },
  { id: "services", key: "nav.services" },
  { id: "catalog", key: "nav.products" },
  { id: "portfolio", key: "nav.portfolio" },
  { id: "contact", key: "nav.contact" },
];

const Header = () => {
  const { lang, setLang, t } = useLang();
  const { theme, toggle } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => scrollToSection(id);

  const scrollToMobile = (id) => {
    setMobileOpen(false);
    // Defer past framer-motion AnimatePresence exit (which resets scroll) — see scroll bug RCA.
    setTimeout(() => scrollToSection(id), 360);
  };

  const current = LANGS.find((l) => l.code === lang);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "bg-background/85 backdrop-blur-xl border-b border-gold/15 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <button
          data-testid="brand-logo"
          onClick={() => scrollTo("home")}
          className="flex items-center gap-2.5 leading-none group"
          aria-label="A&K Atelier — accueil"
        >
          <Logo size={scrolled ? 38 : 44} className="transition-all duration-500 shrink-0" />
          <span className="flex flex-col items-start">
            <span className={`font-serif text-xl sm:text-2xl tracking-tight ${scrolled ? "text-foreground" : "text-white"}`}>
              <span className="gold-gradient-text">A&amp;K</span> Atelier
            </span>
            <span className="hidden sm:block text-[8.5px] sm:text-[9px] tracking-[0.28em] uppercase text-gold/70 mt-1 whitespace-nowrap">
              Artisan Créateur de Décors en Plâtre
            </span>
          </span>
        </button>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-${item.id}`}
              onClick={() => scrollTo(item.id)}
              className={`text-xs font-sans uppercase tracking-[0.18em] hover:text-gold transition-colors duration-300 ${
                scrolled ? "text-foreground/80" : "text-white/80"
              }`}
            >
              {t(item.key)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {/* Theme toggle */}
          <button
            data-testid="theme-toggle"
            onClick={toggle}
            aria-label="Toggle theme"
            className={`flex items-center justify-center w-9 h-9 rounded-sm border border-gold/25 hover:text-gold hover:border-gold/50 transition-colors ${
              scrolled ? "text-foreground/90" : "text-white/90"
            }`}
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          {/* Language switcher */}
          <div className="relative">
            <button
              data-testid="lang-switcher"
              onClick={() => setLangOpen((v) => !v)}
              onBlur={() => setTimeout(() => setLangOpen(false), 150)}
              className={`flex items-center gap-1.5 text-xs font-sans font-semibold tracking-widest hover:text-gold transition-colors border border-gold/25 rounded-sm px-3 py-2 ${
                scrolled ? "text-foreground/90" : "text-white/90"
              }`}
            >
              {current?.label}
              <ChevronDown size={13} className={`transition-transform ${langOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.18 }}
                  className="absolute right-0 mt-2 w-40 bg-popover border border-gold/20 rounded-sm overflow-hidden shadow-2xl"
                >
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      data-testid={`lang-option-${l.code}`}
                      onClick={() => {
                        setLang(l.code);
                        setLangOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 text-sm font-sans flex items-center justify-between transition-colors ${
                        lang === l.code ? "text-gold bg-gold/5" : "text-foreground/70 hover:bg-muted hover:text-foreground"
                      }`}
                    >
                      <span>{l.name}</span>
                      <span className="text-[10px] tracking-widest opacity-60">{l.label}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <AccountMenu scrolled={scrolled} />

          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden ${scrolled ? "text-foreground" : "text-white"}`}
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-background/95 backdrop-blur-xl border-t border-gold/15 overflow-hidden"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {NAV.map((item) => (
                <button
                  key={item.id}
                  data-testid={`mobile-nav-${item.id}`}
                  onClick={() => scrollToMobile(item.id)}
                  className="text-left text-sm font-sans uppercase tracking-[0.18em] text-foreground/85 hover:text-gold transition-colors py-1"
                >
                  {t(item.key)}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
